import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatBadgeModule } from '@angular/material/badge';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DocumentNavigationComponent } from 'app/common/components/document-navigation/document-navigation.component';
import { OrderAttributes } from '@albatrosdeveloper/ave-models-package';
import { OrderOperationTypeEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';

/**
 * UnifiedOrderBadgeComponent
 *
 * Displays a badge indicator for unified orders (orders created by joining multiple source orders).
 * Shows a gray badge with the count of source transactions and a menu with navigation links.
 *
 * Badge is displayed when:
 * - sourceTransactionsCount >= 2 AND
 * - (operationType === ORDER_JOIN OR isUnified === true)
 *
 * @example
 * ```html
 * <app-unified-order-badge [order]="orderData"></app-unified-order-badge>
 * ```
 */
@Component({
  selector: 'app-unified-order-badge',
  standalone: true,
  imports: [
    CommonModule,
    MatBadgeModule,
    MatMenuModule,
    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
    DocumentNavigationComponent
  ],
  template: `
    @if (shouldShowBadge) {
      <button
        mat-icon-button
        [matMenuTriggerFor]="unifiedMenu"
        matTooltip="Pedido unificado - Click para ver origen"
        class="unified-badge-trigger"
      >
        <span
          matBadge="{{ sourceTransactionsCount }}"
          matBadgeSize="medium"
          class="custom-badge"
        ></span>
      </button>

      <mat-menu #unifiedMenu="matMenu">
        <div class="flex flex-col gap-y-1">
          <div class="menu-header p-3" (click)="$event.stopPropagation()">
            Pedidos de Origen ({{ sourceTransactionsCount }})
          </div>
          <div class="source-transactions-list flex gap-x-1 flex-col gap-y-2 p-3">
            @for (source of order?.sourceTransactions; track source.serial) {
              <app-document-navigation
                class="shrink-0 w-fit min-w-fit"
                [documentType]="source?.type?.code"
                [documentSerial]="source?.serial"
                [displayText]="source?.serial"
                [showIcon]="true">
              </app-document-navigation>
            }
          </div>
        </div>
      </mat-menu>
    }
  `,
  styles: [`
    .unified-badge-trigger {
      width: 24px;
      height: 24px;
      line-height: 24px;
    }

    :host ::ng-deep .custom-badge .mat-badge-content {
      background-color: #9E9E9E !important;
    }

    .menu-header {
      font-weight: 500;
      color: rgba(0, 0, 0, 0.6);
      border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    }

    .source-transactions-list {
      max-height: 250px;
      overflow-y: auto;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UnifiedOrderBadgeComponent {
  /**
   * Order data with serial and sourceTransactions
   * Uses Partial<OrderAttributes> for type safety
   */
  @Input() order?: Partial<OrderAttributes>;

  /**
   * Gets the count of sourceTransactions
   * Badge is shown when count >= 2
   */
  get sourceTransactionsCount(): number {
    return this.order?.sourceTransactions?.length ?? 0;
  }

  /**
   * Checks if the order is a unified order by operationType
   */
  get isUnifiedOperationType(): boolean {
    return this.order?.operationType === OrderOperationTypeEnum.ORDER_JOIN;
  }

  /**
   * Checks if the order has isUnified flag set to true
   */
  get hasUnifiedFlag(): boolean {
    return this.order?.isUnified === true;
  }

  /**
   * Determines if the badge should be shown
   * Shows badge when sourceTransactionsCount >= 2 AND
   * (operationType === ORDER_JOIN OR isUnified === true)
   */
  get shouldShowBadge(): boolean {
    const hasMultipleSourceTransactions = this.sourceTransactionsCount >= 2;
    const isUnified = this.isUnifiedOperationType || this.hasUnifiedFlag;
    return hasMultipleSourceTransactions && isUnified;
  }
}
